import { compileCinematicPlan } from '../domain/cinematic-plan';
import type { RidePoint } from '../domain/ride';
import type { CinematicSequence } from '../../../renderer/src/cinematic-sequence';
import type { LiveComposition } from './live-composition';
import type { CameraAttributeValues } from '../map/attribute-camera';
import { CAMERA_TRACKS, cameraLensMillimeters, evaluateAuthoredCameraTrack, resolveCameraKeyframes } from '../domain/camera-keyframes';

/** Camera-only edits can use loaded map terrain while the complete frame settles. */
export function interactiveCamera(previous: CinematicSequence | undefined, next: CinematicSequence | undefined,
  before: LiveComposition | undefined, after: LiveComposition | undefined, progress: number,
  automatic: CameraAttributeValues, platform?: string, points?: readonly RidePoint[]): Partial<CameraAttributeValues> | undefined {
  const stable = (composition: LiveComposition | undefined) => {
    if (!composition) return '';
    const { cameraDistanceId: _lens, cameraAngle: _tilt, ...rest } = composition;
    return JSON.stringify(rest);
  };
  const stableWaypoints = (sequence: CinematicSequence | undefined) => JSON.stringify(sequence?.waypoints.map(key => {
    const { cameraDistance: _lens, cameraAngle: _tilt, ...overrides } = key.overrides;
    return { ...key, overrides };
  }).filter(key => Object.keys(key.overrides).length > 0) ?? []);
  if (stable(before) !== stable(after) || stableWaypoints(previous) !== stableWaypoints(next)
    || (previous?.cameraTracks?.targetLock ?? true) !== (next?.cameraTracks?.targetLock ?? true)) return undefined;
  const values: Partial<CameraAttributeValues> = {};
  const duration = after?.durationSeconds ?? 30;
  const tracks = next?.cameraTracks ? resolveCameraKeyframes(next.cameraTracks, duration) : undefined;
  for (const axis of CAMERA_TRACKS) {
    if (axis === 'rotation' && platform === 'chase') continue;
    if (JSON.stringify(previous?.cameraTracks?.[axis] ?? []) === JSON.stringify(next?.cameraTracks?.[axis] ?? [])) continue;
    if (!tracks?.[axis]?.length) return undefined;
    const value = evaluateAuthoredCameraTrack(tracks, axis, progress * duration, () => automatic[axis]);
    if (value !== undefined) values[axis] = value;
  }
  if (before?.cameraDistanceId !== after?.cameraDistanceId && after) values.lens = cameraLensMillimeters(after.cameraDistanceId);
  if (before?.cameraAngle !== after?.cameraAngle && after?.cameraAngle !== undefined) values.tilt = after.cameraAngle;
  if (JSON.stringify(previous?.waypoints ?? []) !== JSON.stringify(next?.waypoints ?? [])) {
    if (!points || !before || !after) return undefined;
    const scene = (composition: LiveComposition, sequence: CinematicSequence | undefined) => compileCinematicPlan(points, {
      cameraStyleVersion: composition.cameraStyleVersion, cameraStyle: composition.cameraStyleId, cameraDistance: composition.cameraDistanceId,
      cameraAngle: composition.cameraAngle, cameraGimbal: composition.cameraGimbal, mapStyle: composition.mapStyleId,
    }, sequence, { width: 1920, height: 1080, padding: { top: 0, right: 0, bottom: 0, left: 0 } }, duration).evaluate(progress).settings;
    const from = scene(before, previous), to = scene(after, next);
    if (from.cameraDistance !== to.cameraDistance) values.lens = cameraLensMillimeters(to.cameraDistance);
    if (from.cameraAngle !== to.cameraAngle && to.cameraAngle !== null) values.tilt = to.cameraAngle;
  }
  return Object.keys(values).length ? values : undefined;
}
